import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { useAPI } from 'src/util/use-api';
import { QUERIES } from 'src/util/globals';
import type { ProfilesApiProfilesUsersListRequest } from 'src/api-sdk';

// Derived from the generated SDK so the accepted values cannot drift from the
// API contract (the generator emits these as inline unions, not enums).
export type UsersSorting = NonNullable<
  ProfilesApiProfilesUsersListRequest['sorting']
>;
export type UsersSortingOrder = NonNullable<
  ProfilesApiProfilesUsersListRequest['sortingOrder']
>;

type UseUsersParams = {
  enabled?: boolean;
  searchQuery?: string;
  isAdmin?: boolean;
  sorting?: UsersSorting;
  sortingOrder?: UsersSortingOrder;
};

export const useUsers = ({
  enabled = true,
  searchQuery,
  isAdmin,
  sorting,
  sortingOrder,
}: UseUsersParams) => {
  const { API } = useAPI();
  // Normalised once so the cache key and the request cannot disagree: without
  // this, `sortingOrder` with no `sorting` produces a distinct key that maps to
  // an identical request.
  const effectiveSortingOrder = sorting ? sortingOrder : undefined;

  return useInfiniteQuery({
    queryKey: [
      QUERIES.USERS,
      searchQuery,
      isAdmin,
      sorting,
      effectiveSortingOrder,
    ],

    queryFn: async ({ pageParam }) => {
      return (
        await API?.profilesAPI.profilesUsersList({
          nextKey: pageParam ?? undefined,
          query: searchQuery,
          isAdmin,
          sorting,
          sortingOrder: effectiveSortingOrder,
        })
      )?.data;
    },

    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_key ?? undefined,
    enabled,
    placeholderData: keepPreviousData,
  });
};
